import { useEffect, useCallback } from 'react'

import {
  getBufferedActions,
  subscribeToActions,
  clearBufferedActions,
  ActionLogEntry,
} from '../middleware/actionsLoggerMiddleware'
import { DevToolDataUpdater, ReduxActionLog } from '../provider/types'

const DEFAULT_MAX_ACTIONS = 100
const MAX_PAYLOAD_PREVIEW_LENGTH = 200

const createPayloadPreview = (payload: unknown): string | undefined => {
  if (payload === undefined) return undefined

  try {
    const jsonString = JSON.stringify(payload)
    if (jsonString.length <= MAX_PAYLOAD_PREVIEW_LENGTH) {
      return jsonString
    }
    return `${jsonString.substring(0, MAX_PAYLOAD_PREVIEW_LENGTH)}...`
  } catch {
    return '[Unable to serialize payload]'
  }
}

const mapToReduxActionLog = (entry: ActionLogEntry): ReduxActionLog => ({
  id: entry.id,
  type: entry.type,
  timestamp: entry.timestamp,
  payload: entry.payload,
  payloadPreview: createPayloadPreview(entry.payload),
})

export function useReduxActionsMonitor(
  updateData: DevToolDataUpdater,
  isEnabled: boolean,
  maxActions: number = DEFAULT_MAX_ACTIONS
): void {
  const handleNewAction = useCallback(
    (entry: ActionLogEntry) => {
      updateData({
        type: 'ADD_REDUX_ACTION',
        payload: mapToReduxActionLog(entry),
      })
    },
    [updateData]
  )

  useEffect(() => {
    if (!isEnabled) {
      clearBufferedActions()
      return () => {}
    }

    // Load buffered actions from before DevTools mounted
    const bufferedActions = getBufferedActions()
      .slice(0, maxActions)
      .map(mapToReduxActionLog)

    updateData({
      type: 'REDUX_ACTIONS',
      payload: {
        actions: bufferedActions,
        maxActions,
      },
    })

    // Subscribe to new actions
    return subscribeToActions(handleNewAction)
  }, [updateData, isEnabled, maxActions, handleNewAction])
}

export const clearActions = clearBufferedActions

export { DEFAULT_MAX_ACTIONS }
